import React from 'react'
import PropTypes from 'prop-types'
import DenaliScopePicker from '../DenaliAnalysis/DenaliScopePicker'
import DenaliScopeTypePicker from '../DenaliAnalysis/DenaliScopePicker/ScopeTypePicker'
import { useDenaliScopes, useSelectedDenaliScope } from '../DenaliAnalysis/filters/DenaliScopeProvider'
import BlueprintValueSourcePicker from './BlueprintValueSourcePicker'

/**
 * Blueprint presentation renderer for the "Scope Selector" type.
 *
 * Branches on the active scope's source.type:
 *   - 'valueSource' → BlueprintValueSourcePicker (options sourced from a DQL purpose)
 *   - otherwise → DenaliScopePicker (group-membership picker)
 *
 * In complex display mode, a DenaliScopeTypePicker is rendered alongside the
 * value picker so the user can switch scope types. The value picker reruns
 * per scope, so mixed-source pickers work without extra wiring.
 *
 * Must be rendered inside a DenaliScopeProvider (typically via QBPScopeProvider).
 */
function BlueprintScopeSelector ({ blueprintCode, config }) {
  const { scopes } = useDenaliScopes()
  const { scope } = useSelectedDenaliScope()
  const { display, size, hideAvatars, multiSelect } = config ?? {}

  const ValuePicker = scope?.source?.type === 'valueSource'
    ? (
      <BlueprintValueSourcePicker
        blueprintCode={blueprintCode}
        size={size}
        hideAvatars={hideAvatars}
        multiSelect={multiSelect}
      />
    )
    : (
      <DenaliScopePicker
        size={size}
        hideAvatars={hideAvatars}
        multiSelect={multiSelect}
      />
    )

  if (display === 'complex' && scopes.length >= 2) {
    return (
      <div className='d-flex align-items-center gap-sm'>
        <DenaliScopeTypePicker />
        {ValuePicker}
      </div>
    )
  }

  return ValuePicker
}

BlueprintScopeSelector.propTypes = {
  /** Blueprint code under which the picker is defined; needed for value-source option fetches. */
  blueprintCode: PropTypes.string,
  config: PropTypes.shape({
    mode: PropTypes.oneOf(['membership', 'purpose', 'value-source', 'mixed']),
    display: PropTypes.oneOf(['simple', 'complex']),
    multiSelect: PropTypes.bool,
    hideAvatars: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'lg'])
  })
}

export default BlueprintScopeSelector
